import React, { useEffect, useState } from "react";
import { API_BASE } from "../api/config";
import "./WarehouseDamagePanel.css";

function formatQty(value) {
  return Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  });
}

function formatDate(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

function isWarehouseUser(user) {
  return ["WAREHOUSE_STAFF", "WAREHOUSE_MANAGER"].includes(
    String(user?.role || "").toUpperCase()
  );
}

export default function WarehouseDamagePanel({ user, selectedLocation }) {
  const [status, setStatus] = useState("APPROVED_DAMAGE");
  const [rows, setRows] = useState([]);
  const [registerRows, setRegisterRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  async function loadDamageRequests() {
    if (!user?.username || !selectedLocation) return;

    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        username: user.username,
        location: selectedLocation,
        status,
        limit: "100",
      });

      const response = await fetch(`${API_BASE}/api/erp/damage-requests?${params.toString()}`);
      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Failed to load damage requests");
      }

      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      setError(err.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  async function loadDamageRegister() {
    if (!user?.username || !selectedLocation) return;

    try {
      const params = new URLSearchParams({
        username: user.username,
        location: selectedLocation,
        status: "ALL",
        limit: "50",
      });

      const response = await fetch(`${API_BASE}/api/erp/damage-register?${params.toString()}`);
      const data = await response.json();

      if (!response.ok || data.success === false) return;

      setRegisterRows(Array.isArray(data.rows) ? data.rows : []);
    } catch {
      setRegisterRows([]);
    }
  }

  async function receiveDamage(row) {
    try {
      setActionLoading(row.id);
      setError("");
      setSuccessMsg("");

      const response = await fetch(`${API_BASE}/api/erp/damage-requests/${row.id}/receive`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username: user.username,
          receiveNote: "Received from warehouse UI",
        }),
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data.message || "Receive damage failed");
      }

      setSuccessMsg(data.message || "Damage received");
      await loadDamageRequests();
      await loadDamageRegister();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading("");
    }
  }

  useEffect(() => {
    loadDamageRequests();
    loadDamageRegister();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username, selectedLocation, status]);

  return (
    <div className="warehouseDamagePanel">
      <div className="warehouseDamagePanel__head">
        <div>
          <div className="warehouseDamagePanel__tag">⚠️ DAMAGE</div>
          <h2>Warehouse Damage Receive / Register</h2>
          <p>Office approve ပြီးသား damage item တွေကို warehouse မှ receive လုပ်ရန်။</p>
        </div>

        <button type="button" onClick={() => { loadDamageRequests(); loadDamageRegister(); }} disabled={loading}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      {error && <div className="warehouseDamageAlert warehouseDamageAlert--error">{error}</div>}
      {successMsg && <div className="warehouseDamageAlert warehouseDamageAlert--success">{successMsg}</div>}

      <div className="warehouseDamageFilter">
        <label>
          <span>Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="APPROVED_DAMAGE">Approved - Need Receive</option>
            <option value="RECEIVED_DAMAGE">Received Damage</option>
            <option value="PENDING_REVIEW">Pending Review</option>
            <option value="ALL">All</option>
          </select>
        </label>
      </div>

      <div className="warehouseDamageList">
        {rows.length === 0 ? (
          <div className="warehouseDamageEmpty">No damage request found.</div>
        ) : (
          rows.map((row) => (
            <div key={row.id} className="warehouseDamageCard">
              <div className="warehouseDamageCard__top">
                <div>
                  <b>{row.damageNo}</b>
                  <span>{row.reason || "-"}</span>
                </div>

                <strong className={`damageStatus-${row.status}`}>{row.status}</strong>
              </div>

              <div className="warehouseDamageRoute">
                <span>{row.sourceLocationCode}</span>
                <b>→</b>
                <span>{row.warehouseLocationCode}</span>
              </div>

              <div className="warehouseDamageMeta">
                <span>Items: {row.itemRows}</span>
                <span>Total Qty: {formatQty(row.totalQty)}</span>
                <span>Requested: {formatDate(row.requestedAt)}</span>
              </div>

              <div className="warehouseDamageItems">
                {(row.items || []).slice(0, 6).map((item) => (
                  <span key={item.id}>
                    {item.productCode} / {item.color || "-"} / {item.size} × {formatQty(item.qty)}
                  </span>
                ))}
              </div>

              {isWarehouseUser(user) && row.status === "APPROVED_DAMAGE" && (
                <div className="warehouseDamageActions">
                  <button
                    type="button"
                    onClick={() => receiveDamage(row)}
                    disabled={actionLoading === row.id}
                  >
                    {actionLoading === row.id ? "Receiving..." : "📦 Receive Damage"}
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="warehouseDamageRegister">
        <h3>Damage Register Stock</h3>

        {registerRows.length === 0 ? (
          <div className="warehouseDamageEmpty">No damage register rows yet.</div>
        ) : (
          <table className="warehouseDamageTable">
            <thead>
              <tr>
                <th>Damage No</th>
                <th>Source</th>
                <th>Code</th>
                <th>Color</th>
                <th>Size</th>
                <th>Qty</th>
                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {registerRows.map((row) => (
                <tr key={row.id}>
                  <td>{row.damageNo}</td>
                  <td>{row.sourceLocationCode}</td>
                  <td>{row.productCode}</td>
                  <td>{row.color || "-"}</td>
                  <td>{row.size}</td>
                  <td>{formatQty(row.qty)}</td>
                  <td>{row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
